import {
	type CollectionModel,
	type CollectionModelFieldSelectList,
	type CollectionModelFieldSubForm,
	type CollectionModelFieldVector,
	IkePoleForemanFieldIds,
	IPFSpanType,
	PoleFieldIds,
	SpanSubFormAdaptor,
	SpanType,
} from '@ikegps/core-shared';
import { IkeFieldType } from '@ikegps/core-api';
import { action, computed, override, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import type { SubFormLink } from '../../services/subform.link/subform.link.service';
import { AppStore } from '../../store';
import { type MultiChooserGroup, type MultiChooserGroupItem, MultiChooserState } from './multi.list.chooser.state';

export class SpanLinkChooserState extends MultiChooserState {
	collection: CollectionModel;

	constructor(collection: CollectionModel) {
		super(Translate(T.SPAN));

		makeObservable(this);

		this.collection = collection;
	}

	@computed override get groups(): MultiChooserGroup[] {
		const spanLinkRecords = this.nearbySpans;

		if (spanLinkRecords == null) {
			return null;
		}

		if (spanLinkRecords.length === 0) {
			this.setError(T.SPAN_LINK_NO_MATCH);
			return [];
		}

		this.setError(null);

		const items: MultiChooserGroupItem[] = [];
		for (const spanLinkRecord of spanLinkRecords) {
			const collection = spanLinkRecord.collection;
			const spanInstance = spanLinkRecord.instance;
			const collectionTitle = this.getCollectionTitle(collection);
			items.push({
				key: collection.id,
				value: `${collection.schema.titleField.displayName} = ${collectionTitle}, ${spanInstance.title}`,
			});
		}

		return [
			{
				title: Translate(T.SPAN_LINK_SELECT_CONTENT),
				items,
				selected: items.length > 0 ? items[0].key : null,
			},
		];
	}

	@action selectDefaultValue() {
		const spanLinkRecords = this.nearbySpans;

		if (spanLinkRecords == null || spanLinkRecords.length === 0) {
			this.setValue(null);
		} else {
			this.setValue([spanLinkRecords[0].collection.id]);
		}
	}

	@computed private get nearbySpans(): SubFormLink[] | null {
		const tagSummary = AppStore.TagSummary.get(this.collection.jobId);
		if (tagSummary == null || tagSummary.revisions == null) {
			return null;
		}

		const collections: CollectionModel[] = [];
		for (const collectionRevision of tagSummary.revisions) {
			const collection = AppStore.Collection.get(collectionRevision);
			if (collection != null) {
				collections.push(collection);
			}
		}

		if (collections.length === 0) {
			return null;
		}

		// Build set of collection IDs that this collection already has span links to.
		// Walk the same subform fields that findLinks uses, get the vector field ID
		// from the provider rules, and check for existing collection links.
		const linkedCollectionIds = new Set<string>();
		for (const fieldSchema of this.collection.schema.fields) {
			if (fieldSchema.type !== IkeFieldType.FORM) {
				continue;
			}
			const subform = this.collection.getField<CollectionModelFieldSubForm>(fieldSchema.id);
			const rules = Services.SubFormLink.getRules(subform.schema);
			if (rules == null || !subform.hasValue()) {
				continue;
			}
			for (const instance of subform.value) {
				const vectorField = instance.getOptionalField<CollectionModelFieldVector>(rules.vector);
				if (vectorField?.isCollectionLink() && vectorField.stateValue?.collectionId) {
					linkedCollectionIds.add(vectorField.stateValue.collectionId);
				}
			}
		}

		const candidateCollections = collections.filter(
			(collection) => collection.id !== this.collection.id && !linkedCollectionIds.has(collection.id)
		);

		const links = Services.SubFormLink.findLinks(this.collection, candidateCollections);

		// Only fore spans & other spans should be shown as copyable options
		return links.filter((link) => {
			let spanTypeValue: string | null = null;

			try {
				const spanSubForm = new SpanSubFormAdaptor(link.instance);
				spanTypeValue = spanSubForm.spanType;
			} catch (_e) {
				// If the subform instance is not using the new span subform type fall back on hardcoded fields as a possibility
				const typeField =
					link.instance.getOptionalField<CollectionModelFieldSelectList>(PoleFieldIds.SPAN.TYPE) ||
					link.instance.getOptionalField<CollectionModelFieldSelectList>(IkePoleForemanFieldIds.SPAN_TYPE);
				spanTypeValue = typeField?.value ?? null;
			}

			return (
				spanTypeValue === SpanType.NextPole ||
				spanTypeValue === IPFSpanType.FORE_SPAN ||
				spanTypeValue === SpanType.PreviousPole ||
				spanTypeValue === IPFSpanType.BACK_SPAN ||
				spanTypeValue === SpanType.Other ||
				spanTypeValue === IPFSpanType.OTHER
			);
		});
	}

	@override override setValue(value: string[]): void {
		this._value = value;
	}

	getSelectedSpanLinkRecord(): SubFormLink | null {
		if (this.value == null) {
			return null;
		}

		return this.nearbySpans?.find((record) => record.collection.id === this.value[0]) ?? null;
	}

	private getCollectionTitle(collection: CollectionModel): string {
		const title = collection.title;
		return title != null ? title : Translate(T.COLLECTION_MISSING_TITLE);
	}
}
